import React, { useCallback } from 'react';
import { GanttDragTooltip } from './GanttBarTooltip';
import { formatDate } from '../../utils/dateFormat';
import { parseDate, daysDiff } from '../../utils/ganttDates';

/** The attribute a chart puts on the ONE element it is dragging right now. */
export const GANTT_DRAGGING_ATTR = 'data-gantt-dragging';

/**
 * THE live-dates card for a task-chart drag (owner 2026-10-12: *"a little tool
 * tip showing the live dates a bit like we have on"* the Crew Gantt). It is
 * `GanttDragTooltip` — the Crew Gantt's own drag card, same placement rule,
 * tracking the dragged element — with the dates a task Gantt drags:
 *
 *   a task bar   `{ title, start, due, oldStart, oldDue }` → new span + length
 *   a milestone  `{ title, date, oldDate }`                → the day it lands on
 *
 * The chart marks the element it is dragging with `GANTT_DRAGGING_ATTR` and
 * passes `containerRef` (the pane holding its bars); the card finds the anchor
 * there. Render it only while a drag is live. Used by the cross-team task
 * Gantt (the gag panel's Timeline, My Tasks) and the Breakdown Timeline.
 */
export default function GanttDragDatesTooltip({ containerRef, getMinTop, drag, testId = 'gantt-drag-dates' }) {
  const getAnchor = useCallback(
    () => (containerRef.current ? containerRef.current.querySelector(`[${GANTT_DRAGGING_ATTR}]`) : null),
    [containerRef],
  );
  if (!drag) return null;
  const isSpan = drag.date === undefined;
  const moved = isSpan ? (drag.start !== drag.oldStart || drag.due !== drag.oldDue) : drag.date !== drag.oldDate;
  const s = isSpan ? parseDate(drag.start) : null;
  const d = isSpan ? parseDate(drag.due) : null;
  const days = s && d ? daysDiff(s, d) + 1 : null;
  return (
    <GanttDragTooltip getAnchor={getAnchor} getMinTop={getMinTop} testId={testId}>
      <div className={`bg-gray-900 text-white text-xs rounded-lg shadow-xl px-3 py-2 border-2 ${moved ? 'border-blue-400' : 'border-gray-500'}`}>
        <div className="font-semibold mb-1 max-w-[16rem] truncate">{drag.title}</div>
        {isSpan ? (
          <div className="whitespace-nowrap">
            <span className="font-semibold">{formatDate(drag.start)}</span>
            <span className="text-gray-400 mx-1">→</span>
            <span className="font-semibold">{formatDate(drag.due)}</span>
            {days != null && <span className="text-gray-400 ml-2">{days} {days === 1 ? 'day' : 'days'}</span>}
          </div>
        ) : (
          <div className="whitespace-nowrap font-semibold">{formatDate(drag.date)}</div>
        )}
        {moved && (
          <div className="whitespace-nowrap text-gray-400 mt-0.5">
            was {isSpan ? `${formatDate(drag.oldStart)} → ${formatDate(drag.oldDue)}` : formatDate(drag.oldDate)}
          </div>
        )}
      </div>
    </GanttDragTooltip>
  );
}
